import React, { useState, useContext, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
  Typography,
  Box,
  Collapse,
  IconButton,
  useTheme,
  alpha,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Chip,
  Autocomplete,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOnOutlined as LocationIcon,
  AssignmentIndOutlined as ProfileIcon,
  CalendarMonth as CalendarMonthIcon,
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import { DataContext } from "../../contexts/DataProvider";
import { fetchAllContacts, checkDuplicateContact, createContact } from "../../services/contacts.service";
import { linkContact } from "../../services/relationships.service";
import { createClient, updateClient } from "../../services/clients.service";
import { useAlert } from "../../functions/useAlert";
import { createClientGraph, updateContactGraph } from "../../functions/knowledgeGraphApi";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { getContactConfig, splitClientName } from "../../utils/contactConfig";

dayjs.extend(isBetween);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^(\+61[0-9]{9}|0[0-9]{9}|\+?[0-9]{10,15})$/;

// ── Types ─────────────────────────────────────────────────────────────────────

interface ContactEntry {
  id: string;
  mode: "new" | "existing";
  // new contact fields
  firstName: string;
  surname: string;
  email: string;
  phone: string;
  relationshipType: string;
  // validation
  errors: Record<string, string>;
  // duplicate state
  checkingDuplicate: boolean;
  duplicateFound: boolean | null; // null = not checked
  duplicateInfo: any;
  forceCreate: boolean;
  // existing contact
  existingContact: any | null;
  // ui
  expanded: boolean;
}

interface ClientModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  clientData?: any;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const initialClientState = {
  name: "",
  entityType: "Individual",
  ofa: { agreementSignedDate: "" },
  address: { line1: "", line2: "", city: "", state: "", zipCode: "", country: "" },
  profile: { primaryIndustry: "", companySize: "", clientStatus: "Active" },
};

const makeNewContact = (defaultRelationship = "Spouse"): ContactEntry => ({
  id: `${Date.now()}-${Math.random()}`,
  mode: "new",
  firstName: "",
  surname: "",
  email: "",
  phone: "",
  relationshipType: defaultRelationship,
  errors: {},
  checkingDuplicate: false,
  duplicateFound: null,
  duplicateInfo: null,
  forceCreate: false,
  existingContact: null,
  expanded: true,
});

// ── ContactCard sub-component ─────────────────────────────────────────────────

interface ContactCardProps {
  entry: ContactEntry;
  index: number;
  allContacts: any[];
  loadingContacts: boolean;
  allowedRelationships: string[];
  onLoadContacts: () => void;
  onUpdate: (updates: Partial<ContactEntry>) => void;
  onRemove?: () => void;
  onCheckDuplicate: () => void;
  onUseExisting: (dup: any) => void;
  onForceCreate: () => void;
}

const ContactCard: React.FC<ContactCardProps> = ({
  entry, index, allContacts, loadingContacts, allowedRelationships,
  onLoadContacts, onUpdate, onRemove, onCheckDuplicate, onUseExisting, onForceCreate,
}) => {
  const theme = useTheme();

  const displayName =
    entry.mode === "existing" && entry.existingContact
      ? entry.existingContact.name
      : `${entry.firstName} ${entry.surname}`.trim() || `Contact ${index + 1}`;

  const hasDuplicate = entry.duplicateFound === true && !entry.forceCreate;

  const isReady =
    entry.mode === "existing"
      ? !!entry.existingContact
      : !!(entry.firstName && entry.email && !hasDuplicate && Object.keys(entry.errors).length === 0);

  const borderColor = hasDuplicate
    ? theme.palette.warning.main
    : isReady
    ? theme.palette.success.light
    : theme.palette.divider;

  return (
    <Box
      sx={{
        border: `1px solid ${borderColor}`,
        borderRadius: "10px",
        mb: 1.5,
        overflow: "hidden",
        transition: "border-color 200ms",
      }}
    >
      {/* Card header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 1.5,
          py: 0.75,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => onUpdate({ expanded: !entry.expanded })}
      >
        <PersonIcon fontSize="small" color="action" sx={{ mr: 1, flexShrink: 0 }} />
        <Typography variant="body2" fontWeight={500} sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {displayName}
        </Typography>

        {entry.mode === "new" && entry.relationshipType && !entry.expanded && (
          <Chip
            label={entry.relationshipType}
            size="small"
            sx={{ mr: 0.75, height: 18, fontSize: "0.68rem", flexShrink: 0 }}
          />
        )}
        {hasDuplicate && (
          <Chip label="Duplicate" size="small" color="warning" sx={{ mr: 0.75, height: 18, fontSize: "0.68rem", flexShrink: 0 }} />
        )}
        {isReady && !hasDuplicate && (
          <Chip label="Ready" size="small" color="success" variant="outlined" sx={{ mr: 0.75, height: 18, fontSize: "0.68rem", flexShrink: 0 }} />
        )}
        {onRemove && (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            sx={{ mr: 0.25 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton size="small">
          {entry.expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Card body */}
      <Collapse in={entry.expanded}>
        <Box sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          {/* Mode toggle */}
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            <Button
              size="small"
              variant={entry.mode === "new" ? "contained" : "outlined"}
              startIcon={<PersonAddIcon sx={{ fontSize: "0.9rem !important" }} />}
              onClick={() => onUpdate({ mode: "new", existingContact: null, errors: {}, duplicateFound: null })}
              sx={{ flex: 1, py: 0.4, fontSize: "0.75rem" }}
            >
              New contact
            </Button>
            <Button
              size="small"
              variant={entry.mode === "existing" ? "contained" : "outlined"}
              startIcon={<LinkIcon sx={{ fontSize: "0.9rem !important" }} />}
              onClick={() => {
                onLoadContacts();
                onUpdate({ mode: "existing", duplicateFound: null, errors: {} });
              }}
              sx={{ flex: 1, py: 0.4, fontSize: "0.75rem" }}
            >
              Link existing
            </Button>
          </Box>

          {/* New contact form */}
          {entry.mode === "new" && (
            <Grid container spacing={1.25}>
              <Grid item xs={6}>
                <TextField
                  required
                  label="First Name"
                  fullWidth
                  size="small"
                  value={entry.firstName}
                  onChange={(e) => onUpdate({ firstName: e.target.value, duplicateFound: null, forceCreate: false })}
                  error={!!entry.errors.firstName}
                  helperText={entry.errors.firstName}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Last Name"
                  fullWidth
                  size="small"
                  value={entry.surname}
                  onChange={(e) => onUpdate({ surname: e.target.value, duplicateFound: null, forceCreate: false })}
                  error={!!entry.errors.surname}
                  helperText={entry.errors.surname}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  label="Email"
                  fullWidth
                  size="small"
                  type="email"
                  value={entry.email}
                  onChange={(e) => onUpdate({ email: e.target.value, duplicateFound: null, forceCreate: false })}
                  onBlur={onCheckDuplicate}
                  error={!!entry.errors.email}
                  helperText={entry.errors.email}
                  InputProps={{
                    endAdornment: entry.checkingDuplicate ? (
                      <CircularProgress size={14} sx={{ mr: 0.5, flexShrink: 0 }} />
                    ) : null,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Phone"
                  fullWidth
                  size="small"
                  value={entry.phone}
                  onChange={(e) => onUpdate({ phone: e.target.value })}
                  error={!!entry.errors.phone}
                  helperText={entry.errors.phone || "Optional"}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small" required error={!!entry.errors.relationshipType}>
                  <InputLabel>Relationship</InputLabel>
                  <Select
                    value={entry.relationshipType}
                    label="Relationship"
                    onChange={(e) => onUpdate({ relationshipType: e.target.value })}
                  >
                    {allowedRelationships.map((r) => (
                      <MenuItem key={r} value={r}>{r}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Duplicate warning */}
              {hasDuplicate && entry.duplicateInfo && (
                <Grid item xs={12}>
                  <Alert
                    severity="warning"
                    sx={{
                      py: 0.75,
                      "& .MuiAlert-message": { flex: 1 },
                      "& .MuiAlert-action": { pt: 0.5, alignItems: "flex-start" },
                    }}
                    action={
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => onUseExisting(entry.duplicateInfo)}
                          sx={{ fontSize: "0.7rem", py: 0.25, px: 1, whiteSpace: "nowrap" }}
                        >
                          Use existing
                        </Button>
                        <Button
                          size="small"
                          color="warning"
                          onClick={onForceCreate}
                          sx={{ fontSize: "0.7rem", py: 0.25, px: 1, whiteSpace: "nowrap" }}
                        >
                          Create new
                        </Button>
                      </Box>
                    }
                  >
                    <Typography variant="caption" fontWeight={600} display="block">
                      Contact already exists
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {entry.duplicateInfo.name}
                      {entry.duplicateInfo.email ? ` · ${entry.duplicateInfo.email}` : ""}
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}

          {/* Link existing contact */}
          {entry.mode === "existing" && (
            <Box>
              <Autocomplete
                options={allContacts}
                loading={loadingContacts}
                getOptionLabel={(opt) => opt.name || ""}
                value={entry.existingContact}
                onChange={(_, val) => onUpdate({ existingContact: val, errors: {} })}
                filterOptions={(opts, { inputValue }) => {
                  const q = inputValue.toLowerCase();
                  return opts.filter(
                    (o) =>
                      o.name?.toLowerCase().includes(q) ||
                      o.email?.toLowerCase().includes(q)
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search contacts"
                    size="small"
                    error={!!entry.errors.existingContact}
                    helperText={entry.errors.existingContact}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingContacts ? <CircularProgress size={14} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, opt) => (
                  <Box component="li" {...props} key={opt._id}>
                    <Box>
                      <Typography variant="body2">{opt.name}</Typography>
                      {opt.email && (
                        <Typography variant="caption" color="text.secondary">
                          {opt.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              />
              {entry.existingContact && (
                <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                  <InputLabel>Relationship</InputLabel>
                  <Select
                    value={entry.relationshipType || "Other"}
                    label="Relationship"
                    onChange={(e) => onUpdate({ relationshipType: e.target.value })}
                  >
                    {allowedRelationships.map((r) => (
                      <MenuItem key={r} value={r}>{r}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const ClientModal: React.FC<ClientModalProps> = ({ open, onClose, onSave, clientData }) => {
  const { login } = useContext(DataContext);
  const [setAlert] = useAlert();
  const theme = useTheme();

  const isEditMode = !!clientData;

  const [step, setStep] = useState(1);
  const [slideDir, setSlideDir] = useState<"forward" | "back">("forward");
  const [formData, setFormData] = useState(initialClientState);
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);
  const [contacts, setContacts] = useState<ContactEntry[]>([makeNewContact()]);
  const [isSaving, setIsSaving] = useState(false);

  // Contacts list for "link existing" mode
  const [allContacts, setAllContacts] = useState<any[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactsLoaded, setContactsLoaded] = useState(false);

  // ── Reset on open ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setStep(1);
      setContacts([makeNewContact()]);
      setContactsLoaded(false);
      setAllContacts([]);
      if (clientData) {
        setFormData({
          name: clientData.name || "",
          entityType: clientData.entityType || "Individual",
          ofa: {
            agreementSignedDate: clientData.ofa?.referenceDate
              ? clientData.ofa.referenceDate.split("T")[0]
              : "",
          },
          address: {
            line1: clientData.address?.line1 || "",
            line2: clientData.address?.line2 || "",
            city: clientData.address?.city || "",
            state: clientData.address?.state || "",
            zipCode: clientData.address?.zipCode || "",
            country: clientData.address?.country || "",
          },
          profile: {
            primaryIndustry: clientData.profile?.primaryIndustry || "",
            companySize: clientData.profile?.companySize || "",
            clientStatus: clientData.profile?.clientStatus || "Active",
          },
        });
        setShowAdditionalDetails(!!clientData.ofa?.referenceDate);
      } else {
        setFormData(initialClientState);
        setShowAdditionalDetails(false);
      }
    }
  }, [clientData, open]);

  const loadAllContacts = useCallback(async () => {
    if (contactsLoaded || loadingContacts) return;
    setLoadingContacts(true);
    try {
      const resp: any = await fetchAllContacts();
      if (resp.success) {
        setAllContacts(resp.contacts || []);
        setContactsLoaded(true);
      }
    } catch {
      // Silently fail — user can still manually type
    } finally {
      setLoadingContacts(false);
    }
  }, [contactsLoaded, loadingContacts]);

  const updateContact = (id: string, updates: Partial<ContactEntry>) => {
    setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c)));
  };

  const checkContactDuplicate = async (entry: ContactEntry) => {
    if (entry.mode !== "new" || !entry.email) return;
    const name = `${entry.firstName.trim()} ${entry.surname.trim()}`.trim();
    updateContact(entry.id, { checkingDuplicate: true, duplicateFound: null });
    try {
      const resp: any = await checkDuplicateContact({ name, email: entry.email, phone: entry.phone, orgId: login.data.orgId });
      updateContact(entry.id, {
        checkingDuplicate: false,
        duplicateFound: resp.found ?? false,
        duplicateInfo: resp.found ? resp.duplicates?.[0] : null,
        forceCreate: false,
      });
    } catch {
      updateContact(entry.id, { checkingDuplicate: false, duplicateFound: false });
    }
  };

  // ── Client form helpers ───────────────────────────────────────────────────
  const handleClientChange = (section: string | null, field: string, value: string) => {
    if (section) {
      setFormData((prev: any) => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const validateStep1 = (): boolean => {
    if (!formData.name.trim()) {
      setAlert("Client name is required.", "warning");
      return false;
    }
    if (!/[a-zA-Z]/.test(formData.name.trim())) {
      setAlert("Client name cannot be purely numerical.", "warning");
      return false;
    }
    if (!formData.entityType) {
      setAlert("Entity type is required.", "warning");
      return false;
    }
    return true;
  };

  const handleContinue = async () => {
    if (!validateStep1()) return;
    setIsSaving(true);
    try {
      // Build the initial contact entry based on entity type
      const config = getContactConfig(formData.entityType);
      const base = makeNewContact(config.defaultRelationship);
      if (config.prefillName) {
        const { firstName, surname } = splitClientName(formData.name);
        setContacts([{ ...base, firstName, surname }]);
      } else {
        setContacts([base]);
      }
      setSlideDir("forward");
      setStep(2);
    } catch (error: any) {
      setAlert(error.message || "Something went wrong, please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    setSlideDir("back");
    setStep(1);
  };

  const saveClientOnly = async (): Promise<any> => {
    const payload: any = {
      ...formData,
      ofa: formData.ofa.agreementSignedDate
        ? { referenceDate: formData.ofa.agreementSignedDate }
        : undefined,
      updatedBy: login.data.id,
    };
    if (!isEditMode) {
      payload.orgId = login.data.orgId;
      payload.userId = login.data.id;
      payload.createdBy = login.data.id;
    }
    if (formData.entityType !== "Company") {
      delete payload.profile.primaryIndustry;
      delete payload.profile.companySize;
    }
    const response: any = isEditMode
      ? await updateClient(clientData.id || clientData._id, payload)
      : await createClient(payload);
    if (response.error) throw new Error(response.error);
    return response;
  };

  // ── Edit mode save ────────────────────────────────────────────────────────
  const handleEditSave = async () => {
    if (!validateStep1()) return;
    setIsSaving(true);
    try {
      const client = await saveClientOnly();
      if (client && (client._id || client.id)) {
        createClientGraph(
          { _id: client._id || client.id, name: client.name, entityType: client.entityType,
            orgId: client.orgId || login.data.orgId, contactId: client.contactId,
            address: client.address || {}, profile: client.profile || {} },
          { userId: login.data.id, orgId: login.data.orgId }
        ).catch((err) => console.error("createClientGraph failed:", err));
      }
      setAlert("Client updated successfully!", "success");
      onSave();
      onClose();
    } catch (error: any) {
      setAlert(error.message || "Failed to update client.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // ── Skip contact (create client only) ────────────────────────────────────
  const handleSkipContact = async () => {
    setIsSaving(true);
    try {
      const client = await saveClientOnly();
      const clientId = client._id || client.id;
      if (clientId) {
        createClientGraph(
          { _id: clientId, name: client.name, entityType: client.entityType,
            orgId: client.orgId || login.data.orgId, contactId: client.contactId,
            address: client.address || {}, profile: client.profile || {} },
          { userId: login.data.id, orgId: login.data.orgId }
        ).catch((err) => console.error("createClientGraph failed:", err));
      }
      setAlert("Client created successfully!", "success");
      onSave();
      onClose();
    } catch (error: any) {
      setAlert(error.message || "Failed to create client.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // ── Create client + all contacts ──────────────────────────────────────────
  const handleFinish = async () => {
    // Guard: prevent double-submission (e.g. accidental double-click)
    if (isSaving) return;
    setIsSaving(true);
    try {
    // 1. Validate all contact entries
    let hasErrors = false;
    const validated = contacts.map((c) => {
      if (c.mode === "existing") {
        if (!c.existingContact) {
          hasErrors = true;
          return { ...c, errors: { existingContact: "Please select a contact." }, expanded: true };
        }
        return { ...c, errors: {} };
      }
      const errors: Record<string, string> = {};
      if (!c.firstName.trim()) errors.firstName = "First name is required.";
      if (!c.email.trim()) errors.email = "Email is required.";
      else if (!EMAIL_REGEX.test(c.email)) errors.email = "Enter a valid email.";
      if (c.phone && !PHONE_REGEX.test(c.phone.replace(/[\s\-()/]/g, "")))
        errors.phone = "Enter a valid phone number.";
      if (!c.relationshipType) errors.relationshipType = "Relationship type is required.";
      if (Object.keys(errors).length > 0) hasErrors = true;
      return { ...c, errors, expanded: Object.keys(errors).length > 0 ? true : c.expanded };
    });
    if (hasErrors) {
      setContacts(validated);
      return;
    }

    // 2. Pre-check all new contacts for duplicates (prevents partial failure)
    const withChecks = await Promise.all(
      validated.map(async (c) => {
        if (c.mode !== "new" || c.forceCreate || c.duplicateFound === false) return c;
        if (c.duplicateFound === true) return c; // already flagged
        const name = `${c.firstName.trim()} ${c.surname.trim()}`.trim();
        try {
          const resp: any = await checkDuplicateContact({ name, email: c.email, phone: c.phone, orgId: login.data.orgId });
          return {
            ...c,
            duplicateFound: resp.found ?? false,
            duplicateInfo: resp.found ? resp.duplicates?.[0] : null,
          };
        } catch {
          return { ...c, duplicateFound: false };
        }
      })
    );

    const unresolved = withChecks.filter(
      (c) => c.mode === "new" && c.duplicateFound === true && !c.forceCreate
    );
    if (unresolved.length > 0) {
      setContacts(withChecks.map((c) =>
        c.duplicateFound && !c.forceCreate ? { ...c, expanded: true } : c
      ));
      return; // User must resolve duplicate(s) before proceeding
    }

      // 3. Create client
      const client = await saveClientOnly();
      const clientId = client._id || client.id;
      if (clientId) {
        createClientGraph(
          { _id: clientId, name: client.name, entityType: client.entityType,
            orgId: client.orgId || login.data.orgId, contactId: client.contactId,
            address: client.address || {}, profile: client.profile || {} },
          { userId: login.data.id, orgId: login.data.orgId }
        ).catch((err) => console.error("createClientGraph failed:", err));
      }

      // 4. Create and/or link each contact
      const results = await Promise.allSettled(
        withChecks.map(async (c, index) => {
          let contactId: string;
          if (c.mode === "existing") {
            contactId = c.existingContact._id || c.existingContact.id;
          } else {
            const name = `${c.firstName.trim()} ${c.surname.trim()}`.trim();
            const resp: any = await createContact({
              name,
              email: c.email.trim(),
              phone: c.phone.trim() || undefined,
              orgId: login.data.orgId,
            }, c.forceCreate);
            if (!resp.success) throw new Error(resp.error || resp.message || "Failed to create contact");
            contactId = resp.contact?._id || resp.contact?.id;
          }
          if (!contactId) throw new Error("No contact ID returned");

          const relType = c.relationshipType || "Other";

          // First contact in the list becomes the primary contact for this client.
          // forcePrimary ensures the backend promotes it even if another contact
          // was previously linked (shouldn't happen on a brand-new client, but
          // this makes the operation idempotent and safe).
          const isFirstContact = index === 0;
          const linkPayload = {
            clientId,
            contactId,
            relationshipType: relType,
            isPrimary: isFirstContact,
            orgId: login.data.orgId,
            userId: login.data.id,
            forcePrimary: isFirstContact,
          };
          await linkContact(linkPayload);
          updateContactGraph({ ...linkPayload, _id: contactId }).catch(() => {});
        })
      );

      const failures = results.filter((r) => r.status === "rejected");
      if (failures.length > 0) {
        setAlert(
          `Client created, but ${failures.length} contact(s) failed to link. Add them from the client profile.`,
          "warning"
        );
      } else {
        setAlert("Client and contacts created successfully!", "success");
      }
      onSave();
      onClose();
    } catch (error: any) {
      setAlert(error.message || "Failed to create client.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const boxStyle = {
    bgcolor: alpha(theme.palette.primary.main, 0.04),
    p: 2.5,
    borderRadius: "12px",
    mb: 2,
  };
  const sectionTitleStyle = {
    fontWeight: "bold",
    color: "primary.main",
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 2,
  };
  const slideAnimation =
    slideDir === "forward"
      ? {
          animation: "slideFromRight 200ms ease-out both",
          "@keyframes slideFromRight": {
            from: { transform: "translateX(24px)", opacity: 0 },
            to: { transform: "translateX(0)", opacity: 1 },
          },
        }
      : {
          animation: "slideFromLeft 200ms ease-out both",
          "@keyframes slideFromLeft": {
            from: { transform: "translateX(-24px)", opacity: 0 },
            to: { transform: "translateX(0)", opacity: 1 },
          },
        };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: "100%", maxWidth: 560 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <BusinessIcon color="primary" />
        {isEditMode ? "Edit Client" : "Add New Client"}
      </DialogTitle>

      {!isEditMode && (
        <Box sx={{ px: 4, pb: 1 }}>
          <Stepper activeStep={step - 1} alternativeLabel>
            <Step><StepLabel>Client Details</StepLabel></Step>
            <Step><StepLabel>Add Contacts</StepLabel></Step>
          </Stepper>
        </Box>
      )}

      <DialogContent sx={{ overflowX: "hidden", overflowY: "auto", maxHeight: "72vh", minHeight: 240 }}>
        {/* ── EDIT MODE ─────────────────────────────────────────────────────── */}
        {isEditMode && (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                required autoFocus label="Client Name" fullWidth
                value={formData.name}
                onChange={(e) => handleClientChange(null, "name", e.target.value)}
                InputProps={{ startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} /> }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Entity Type</InputLabel>
                <Select value={formData.entityType} label="Entity Type"
                  onChange={(e) => handleClientChange(null, "entityType", e.target.value)}>
                  {["Individual", "SoleTrader", "Company", "Family", "Trust"].map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between"
                onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
                sx={{ cursor: "pointer", py: 1, mt: 1, userSelect: "none" }}>
                <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                  {showAdditionalDetails ? "Hide Additional Details" : "Show Additional Details"}
                </Typography>
                <IconButton size="small" color="primary">
                  {showAdditionalDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={showAdditionalDetails}>
                <Box sx={boxStyle}>
                  <Typography variant="subtitle1" sx={sectionTitleStyle}>
                    <CalendarMonthIcon fontSize="small" /> Reference Date
                  </Typography>
                  <TextField label="Reference Date" type="date" fullWidth size="small"
                    value={formData.ofa.agreementSignedDate}
                    onChange={(e) =>
                      setFormData((prev: any) => ({ ...prev, ofa: { ...prev.ofa, agreementSignedDate: e.target.value } }))
                    }
                    InputLabelProps={{ shrink: true }}
                    helperText="Set the reference date — Agreement Signed (−1 yr) and FDS Deadline (−60 days) are auto-calculated"
                    sx={{ bgcolor: "background.paper" }}
                  />
                </Box>
                <Box sx={boxStyle}>
                  <Typography variant="subtitle1" sx={sectionTitleStyle}>
                    <LocationIcon fontSize="small" /> Location Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}><TextField label="Address Line 1" fullWidth size="small" sx={{ bgcolor: "background.paper" }} value={formData.address.line1} onChange={(e) => handleClientChange("address", "line1", e.target.value)} /></Grid>
                    <Grid item xs={12}><TextField label="Address Line 2" fullWidth size="small" sx={{ bgcolor: "background.paper" }} value={formData.address.line2} onChange={(e) => handleClientChange("address", "line2", e.target.value)} /></Grid>
                    <Grid item xs={6}><TextField label="City" fullWidth size="small" sx={{ bgcolor: "background.paper" }} value={formData.address.city} onChange={(e) => handleClientChange("address", "city", e.target.value)} /></Grid>
                    <Grid item xs={6}><TextField label="State" fullWidth size="small" sx={{ bgcolor: "background.paper" }} value={formData.address.state} onChange={(e) => handleClientChange("address", "state", e.target.value)} /></Grid>
                    <Grid item xs={6}><TextField label="ZIP Code" fullWidth size="small" sx={{ bgcolor: "background.paper" }} value={formData.address.zipCode} onChange={(e) => handleClientChange("address", "zipCode", e.target.value)} /></Grid>
                    <Grid item xs={6}><TextField label="Country" fullWidth size="small" sx={{ bgcolor: "background.paper" }} value={formData.address.country} onChange={(e) => handleClientChange("address", "country", e.target.value)} /></Grid>
                  </Grid>
                </Box>
                <Box sx={{ ...boxStyle, mb: 0 }}>
                  <Typography variant="subtitle1" sx={sectionTitleStyle}>
                    <ProfileIcon fontSize="small" /> Entity Profile
                  </Typography>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Client Status</InputLabel>
                    <Select value={formData.profile.clientStatus} label="Client Status" sx={{ bgcolor: "background.paper" }}
                      onChange={(e) => handleClientChange("profile", "clientStatus", e.target.value)}>
                      {["Lead", "Prospect", "Onboarding", "Active", "Passive", "Inactive", "Deceased"].map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Collapse>
            </Grid>
          </Grid>
        )}

        {/* ── STEP 1: Client essentials ──────────────────────────────────────── */}
        {!isEditMode && step === 1 && (
          <Box key="step1" sx={slideAnimation}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  required autoFocus label="Client Name" fullWidth
                  value={formData.name}
                  onChange={(e) => handleClientChange(null, "name", e.target.value)}
                  InputProps={{ startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} /> }}
                  helperText="This is the name you'll use to identify this client."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Entity Type</InputLabel>
                  <Select value={formData.entityType} label="Entity Type"
                    onChange={(e) => handleClientChange(null, "entityType", e.target.value)}>
                    {["Individual", "SoleTrader", "Company", "Family", "Trust"].map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Address, reference dates, and other details can be added from the client profile after creation.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ── STEP 2: Add contacts ───────────────────────────────────────────── */}
        {!isEditMode && step === 2 && (() => {
          const config = getContactConfig(formData.entityType);
          const canAddMore = contacts.length < config.maxContacts;
          return (
            <Box key="step2" sx={slideAnimation}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <PersonAddIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">
                  {config.hint}
                </Typography>
              </Box>

              {contacts.map((c, idx) => (
                <ContactCard
                  key={c.id}
                  entry={c}
                  index={idx}
                  allContacts={allContacts}
                  loadingContacts={loadingContacts}
                  allowedRelationships={config.relationships}
                  onLoadContacts={loadAllContacts}
                  onUpdate={(updates) => updateContact(c.id, updates)}
                  onRemove={contacts.length > 1 ? () => setContacts((prev) => prev.filter((x) => x.id !== c.id)) : undefined}
                  onCheckDuplicate={() => checkContactDuplicate(c)}
                  onUseExisting={(dup) => {
                    loadAllContacts();
                    updateContact(c.id, {
                      mode: "existing",
                      existingContact: dup,
                      duplicateFound: null,
                      forceCreate: false,
                      expanded: true,
                    });
                  }}
                  onForceCreate={() => updateContact(c.id, { forceCreate: true, duplicateFound: false })}
                />
              ))}

              {canAddMore && (
                <Button
                  startIcon={<AddIcon />}
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={() => setContacts((prev) => [...prev, makeNewContact(config.defaultRelationship)])}
                  sx={{ mt: 0.5 }}
                >
                  Add another contact
                </Button>
              )}
            </Box>
          );
        })()}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between" }}>
        {/* Edit mode */}
        {isEditMode && (
          <>
            <Button onClick={onClose} variant="outlined" color="inherit">Cancel</Button>
            <Button onClick={handleEditSave} variant="contained" disabled={isSaving}>
              {isSaving ? <CircularProgress size={20} color="inherit" /> : "Update Client"}
            </Button>
          </>
        )}

        {/* Step 1 */}
        {!isEditMode && step === 1 && (
          <>
            <Button onClick={onClose} variant="outlined" color="inherit" disabled={isSaving}>Cancel</Button>
            <Button onClick={handleContinue} variant="contained" disabled={isSaving}>
              {isSaving ? <CircularProgress size={20} color="inherit" /> : "Continue"}
            </Button>
          </>
        )}

        {/* Step 2 */}
        {!isEditMode && step === 2 && (
          <>
            <Button onClick={handleBack} startIcon={<ArrowBackIcon />} variant="outlined" color="inherit" disabled={isSaving}>
              Back
            </Button>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button onClick={handleSkipContact} variant="outlined" color="inherit" disabled={isSaving}>
                Skip for now
              </Button>
              <Button onClick={handleFinish} variant="contained" disabled={isSaving}>
                {isSaving ? <CircularProgress size={20} color="inherit" /> : "Create Client"}
              </Button>
            </Box>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ClientModal;
